/**
 * AdaptivePhoto Component
 *
 * Automatically selects and displays the appropriate photo resolution (thumbnail/medium/full)
 * based on container display size using ResizeObserver API.
 *
 * Features:
 * - Automatic variant selection based on container dimensions
 * - Intelligent fallback when preferred variant is missing
 * - Optional debug overlay with dimension validation
 * - Debounced resize handling (150ms) for optimal performance
 *
 * @see specs/041-adaptive-photo-control/spec.md
 */

import { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { DebugModeOnly } from '../DebugModeOnly';
import { AdminAwareIdLink } from '../AdminAwareIdLink';
import { useActivityTracking } from '../../hooks/useActivityTracking';
import logger from '../../utils/logger';

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Image variant type
 */
export type VariantType = 'thumbnail' | 'small' | 'medium' | 'large' | 'full';

/**
 * Image variant metadata
 */
export interface ImageVariant {
  id: string;
  variantType: VariantType;
  storageUrl: string;
  /** Flickr-style public URL via redirect endpoint (Feature 077) */
  publicUrl?: string | null;
  width: number;
  height: number;
  fileSize: number;
  format: string;
}

/**
 * AdaptivePhoto component props
 */
export interface AdaptivePhotoProps {
  /**
   * Photo object containing all variant URLs
   * Required: variants array with thumbnail/medium/full
   */
  photo: {
    id: string;
    variants: ImageVariant[];
    description?: string | null;
  };

  /**
   * Alt text for accessibility
   * Uses photo description if not provided
   */
  alt?: string;

  /**
   * Additional CSS classes for styling the container
   */
  className?: string;

  /**
   * Additional CSS classes for styling the img element
   * Default: "w-full h-full object-contain"
   * Override for special layout needs (e.g., crop tool)
   */
  imgClassName?: string;

  /**
   * Additional inline styles for the img element
   * Useful for dynamic styles like clip-path in comparison views
   */
  imgStyle?: React.CSSProperties;

  /**
   * Enable debug overlay showing variant selection and dimensions
   * Default: false
   */
  showDebug?: boolean;

  /**
   * Callback fired when image loads successfully
   * Includes actual loaded dimensions
   */
  onLoad?: (dimensions: { width: number; height: number }) => void;

  /**
   * Callback fired when image fails to load
   */
  onError?: (error: Error) => void;

  /**
   * Source context for activity tracking (e.g., 'collection', 'critique', 'feed')
   * Used to track where users view photos for analytics
   * Default: 'adaptive_photo'
   */
  trackingSource?: string;

  /**
   * Prefer storageUrl over publicUrl for image loading.
   * Use this for version comparison views where publicUrl is photo-based (not version-specific).
   * Default: false (prefers publicUrl for caching benefits)
   */
  preferStorageUrl?: boolean;
}

/**
 * Internal component state (for documentation purposes)
 * Not exported but reflects the component's useState structure
 */
// interface AdaptivePhotoState {
//   currentVariant: VariantType;
//   containerSize: { width: number; height: number };
//   loadingState: 'idle' | 'loading' | 'loaded' | 'error';
//   loadedDimensions: { width: number; height: number } | null;
//   validationResult: ValidationResult | null;
// }

/**
 * Dimension validation result
 */
interface ValidationResult {
  valid: boolean;
  warning?: string;
  expectedMax?: string;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Variant selection breakpoint thresholds
 * Aligned with image processing pipeline constraints
 */
export const VARIANT_BREAKPOINTS = {
  THUMBNAIL_MAX: 300,   // Max dimension (width or height) for thumbnail
  SMALL_MAX: 800,       // Max width for small variant (042-small-image-variant)
  MEDIUM_MAX: 1920,     // Max width for medium resolution
  LARGE_MAX: 3840,      // Max width for large variant (4K displays)
} as const;

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Select appropriate variant based on container dimensions
 *
 * @param width - Container width in pixels
 * @param height - Container height in pixels
 * @returns Variant type to use
 */
export function selectVariant(width: number, height: number): VariantType {
  const maxDimension = Math.max(width, height);

  // Small displays: Use thumbnail
  if (maxDimension < VARIANT_BREAKPOINTS.THUMBNAIL_MAX) {
    return 'thumbnail';
  }

  // Medium-small displays: Use small variant (300-800px)
  if (width < VARIANT_BREAKPOINTS.SMALL_MAX) {
    return 'small';
  }

  // Medium displays: Use medium resolution (800-1920px)
  if (width < VARIANT_BREAKPOINTS.MEDIUM_MAX) {
    return 'medium';
  }

  // Large displays (4K): Use large resolution (1920-3840px)
  if (width < VARIANT_BREAKPOINTS.LARGE_MAX) {
    return 'large';
  }

  // Very large displays: Use full resolution (>3840px)
  return 'full';
}

/**
 * Check if a specific variant exists in the photo data
 *
 * @param photo - Photo object with variant URLs
 * @param variantType - Variant type to check for
 * @returns True if the variant exists, false otherwise
 */
function checkVariantExists(
  photo: AdaptivePhotoProps['photo'],
  variantType: VariantType
): boolean {
  return photo.variants.some(v => v.variantType === variantType);
}

/**
 * Detect which variant was actually loaded based on the image URL
 *
 * @param photo - Photo object with variant URLs
 * @param loadedUrl - The actual URL that was loaded
 * @returns The variant type that was loaded, or undefined if unknown
 */
function detectLoadedVariant(
  photo: AdaptivePhotoProps['photo'],
  loadedUrl: string
): VariantType | undefined {
  // Normalize URLs for comparison (remove query params, fragments)
  const normalizeUrl = (url: string) => url.split('?')[0].split('#')[0];
  const normalizedLoaded = normalizeUrl(loadedUrl);

  // Check all variants (both storageUrl and publicUrl - Feature 077)
  for (const variant of photo.variants) {
    if (normalizeUrl(variant.storageUrl) === normalizedLoaded) {
      return variant.variantType;
    }
    if (variant.publicUrl && normalizeUrl(variant.publicUrl) === normalizedLoaded) {
      return variant.variantType;
    }
  }

  return undefined;
}

/**
 * Select best variant based on target display dimensions
 *
 * Feature 646: Compares long side (max of width/height) to find smallest
 * variant that provides sufficient resolution for target display size.
 *
 * Algorithm:
 * 1. Calculate target long side: max(targetWidth, targetHeight)
 * 2. For each variant, get long side: max(variant.width, variant.height)
 * 3. Select smallest variant where variant long side >= target long side
 * 4. If none are large enough, select largest available
 *
 * @param variants - Array of available image variants with dimensions
 * @param targetWidth - Target display width in CSS pixels
 * @param targetHeight - Target display height in CSS pixels
 * @returns Best variant type, or undefined if no variants available
 */
export function selectBestVariant(
  variants: Array<{ variantType: VariantType; width: number; height: number }>,
  targetWidth: number,
  targetHeight: number
): VariantType | undefined {
  if (!variants || variants.length === 0) return undefined;

  // Calculate target long side
  const targetLongSide = Math.max(targetWidth, targetHeight);

  // Default to medium if dimensions not available (0, NaN, or very small)
  // This handles test scenarios and edge cases where container hasn't sized yet
  if (!targetLongSide || targetLongSide < 100) {
    return variants.find(v => v.variantType === 'medium')?.variantType ||
           variants.find(v => v.variantType === 'full')?.variantType ||
           variants[variants.length - 1]?.variantType; // Fallback to largest available
  }

  // Find variants with sufficient resolution (long side >= target long side)
  const sufficientVariants = variants
    .map(v => ({
      type: v.variantType,
      longSide: Math.max(v.width, v.height),
    }))
    .filter(v => v.longSide >= targetLongSide)
    .sort((a, b) => a.longSide - b.longSide); // Smallest first

  // Return smallest sufficient variant, or largest available if none are sufficient
  if (sufficientVariants.length > 0) {
    return sufficientVariants[0].type;
  }

  // No variant is large enough - return largest available
  const largestVariant = variants
    .map(v => ({
      type: v.variantType,
      longSide: Math.max(v.width, v.height),
    }))
    .sort((a, b) => b.longSide - a.longSide)[0]; // Largest first

  return largestVariant?.type;
}

/**
 * Get the best URL for a variant
 * Feature 077: Photo URL Redirect - prefers publicUrl for caching benefits
 * @param variant - The image variant
 * @param preferStorageUrl - If true, prefer storageUrl (for version-specific comparisons)
 */
function getBestUrl(variant: ImageVariant | undefined, preferStorageUrl = false): string | undefined {
  if (!variant) return undefined;
  if (preferStorageUrl) {
    return variant.storageUrl || variant.publicUrl || undefined;
  }
  return variant.publicUrl || variant.storageUrl || undefined;
}

/**
 * Get variant URL with intelligent fallback
 *
 * @param photo - Photo object with variant URLs
 * @param preferredVariant - Preferred variant type
 * @param preferStorageUrl - If true, prefer storageUrl over publicUrl (for version comparisons)
 * @returns Variant URL or undefined if none available
 */
export function getVariantUrl(
  photo: AdaptivePhotoProps['photo'],
  preferredVariant: VariantType,
  preferStorageUrl = false
): string | undefined {
  const thumbnailUrl = getBestUrl(photo.variants.find(v => v.variantType === 'thumbnail'), preferStorageUrl);
  const smallUrl = getBestUrl(photo.variants.find(v => v.variantType === 'small'), preferStorageUrl);
  const mediumUrl = getBestUrl(photo.variants.find(v => v.variantType === 'medium'), preferStorageUrl);
  const largeUrl = getBestUrl(photo.variants.find(v => v.variantType === 'large'), preferStorageUrl);
  const fullUrl = getBestUrl(photo.variants.find(v => v.variantType === 'full'), preferStorageUrl);

  switch (preferredVariant) {
    case 'thumbnail':
      // Prefer thumbnail, fallback to small, then medium, then large, then full
      return thumbnailUrl || smallUrl || mediumUrl || largeUrl || fullUrl || undefined;

    case 'small':
      // Prefer small, fallback to medium (upscale), then large, then full, then thumbnail (downscale)
      return smallUrl || mediumUrl || largeUrl || fullUrl || thumbnailUrl || undefined;

    case 'medium':
      // Prefer medium, fallback to large (upscale), then full, then small (downscale), then thumbnail
      return mediumUrl || largeUrl || fullUrl || smallUrl || thumbnailUrl || undefined;

    case 'large':
      // Prefer large, fallback to full (upscale), then medium (downscale), then small, then thumbnail
      return largeUrl || fullUrl || mediumUrl || smallUrl || thumbnailUrl || undefined;

    case 'full':
      // Prefer full, fallback to large, then medium, then small, then thumbnail
      return fullUrl || largeUrl || mediumUrl || smallUrl || thumbnailUrl || undefined;
  }
}

/**
 * Validate loaded image dimensions against expected variant constraints
 *
 * @param actualWidth - Actual loaded image width
 * @param actualHeight - Actual loaded image height
 * @param variantType - Variant type that was loaded
 * @returns Validation result with warning if constraints violated
 */
export function validateDimensions(
  actualWidth: number,
  actualHeight: number,
  variantType: VariantType
): ValidationResult {
  const maxDim = Math.max(actualWidth, actualHeight);

  switch (variantType) {
    case 'thumbnail':
      if (maxDim > VARIANT_BREAKPOINTS.THUMBNAIL_MAX) {
        return {
          valid: false,
          warning: `⚠ Thumbnail ${actualWidth}x${actualHeight} exceeds 300px`,
          expectedMax: '≤300px max dimension',
        };
      }
      return { valid: true };

    case 'small':
      if (actualWidth > VARIANT_BREAKPOINTS.SMALL_MAX) {
        return {
          valid: false,
          warning: `⚠ Small ${actualWidth}x${actualHeight} exceeds 800px width`,
          expectedMax: '≤800px width',
        };
      }
      return { valid: true };

    case 'medium':
      if (actualWidth > VARIANT_BREAKPOINTS.MEDIUM_MAX) {
        return {
          valid: false,
          warning: `⚠ Medium ${actualWidth}x${actualHeight} exceeds 1920px width`,
          expectedMax: '≤1920px width',
        };
      }
      return { valid: true };

    case 'large':
      if (actualWidth > VARIANT_BREAKPOINTS.LARGE_MAX) {
        return {
          valid: false,
          warning: `⚠ Large ${actualWidth}x${actualHeight} exceeds 3840px width`,
          expectedMax: '≤3840px width',
        };
      }
      return { valid: true };

    case 'full':
      // No constraints for full resolution
      return { valid: true };
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Debounce function to limit how often a function is called
 */
function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };

    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}

// ============================================================================
// Component
// ============================================================================

/**
 * AdaptivePhoto - Smart image display with automatic resolution selection
 */
export function AdaptivePhoto({
  photo,
  alt,
  className = '',
  imgClassName = 'relative w-full h-full object-contain',
  imgStyle,
  onLoad: onLoadCallback,
  onError: onErrorCallback,
  trackingSource = 'adaptive_photo',
  preferStorageUrl = false,
}: AdaptivePhotoProps) {
  // ========================================
  // Runtime Validation (CRITICAL: Catch data corruption early)
  // ========================================

  if (!Array.isArray(photo.variants)) {
    logger.error('AdaptivePhoto', '❌ CRITICAL ERROR: photo.variants is not an array!', {
      variantsType: typeof photo.variants,
      variantsValue: photo.variants,
      photo,
    });
    throw new Error(
      `AdaptivePhoto: variants must be an array, got ${typeof photo.variants}. ` +
      `This indicates data corruption - variants was likely JSON.stringify'd. ` +
      `Check API responses, component props, and state management.`
    );
  }

  // Validate each variant has required properties
  photo.variants.forEach((variant, index) => {
    if (!variant.variantType || !variant.storageUrl) {
      logger.error('AdaptivePhoto', `❌ CRITICAL ERROR: variant[${index}] missing required properties!`, variant);
      throw new Error(
        `AdaptivePhoto: variant[${index}] missing variantType or storageUrl. ` +
        `Got: ${JSON.stringify(variant)}`
      );
    }
  });

  // ========================================
  // Activity Tracking
  // ========================================

  // Track photo views for analytics and "new content" discovery
  const { ref: activityTrackingRef } = useActivityTracking({
    activityType: 'view',
    entityType: 'photo',
    entityId: photo.id, // Global ID
    metadata: {
      source: trackingSource,
    },
  });

  // ========================================
  // State
  // ========================================

  const [currentVariant, setCurrentVariant] = useState<VariantType>('thumbnail');
  const [, setContainerSize] = useState({ width: 0, height: 0 });
  const [, setLoadingState] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  const [loadedDimensions, setLoadedDimensions] = useState<{ width: number; height: number } | null>(null);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // Merge containerRef with activityTrackingRef
  const mergedRef = useCallback((element: HTMLDivElement | null) => {
    // Set containerRef (used by ResizeObserver)
    // @ts-expect-error - RefObject.current is technically read-only, but callback refs can assign to it
    containerRef.current = element;

    // Set activityTrackingRef (used by Intersection Observer for view tracking)
    if (activityTrackingRef && element) {
      // @ts-expect-error - RefObject.current is technically read-only, but callback refs can assign to it
      activityTrackingRef.current = element;
    }
  }, [activityTrackingRef]);

  // Validate variants is not stringified (detect data pipeline issues)
  if (typeof photo.variants === 'string') {
    logger.error('AdaptivePhoto',
      '[AdaptivePhoto] Photo variants is stringified - data pipeline issue detected',
      {
        variantsType: typeof photo.variants,
        variantsValue: photo.variants,
        // Include stack trace to help identify where the component is being used
        componentStack: new Error().stack,
      }
    );
  }

  // ========================================
  // Initial Measurement (Synchronous)
  // ========================================

  useLayoutEffect(() => {
    if (containerRef.current) {
      const { width, height } = containerRef.current.getBoundingClientRect();
      setContainerSize({ width, height });
      // Use selectBestVariant if variants have dimension data, otherwise fall back to breakpoint-based selection
      const variantsWithDimensions = photo.variants.filter(v => v.width && v.height);

      if (variantsWithDimensions.length > 0) {
        // Smart selection based on actual variant dimensions (Feature 653: hero images)
        const bestVariant = selectBestVariant(variantsWithDimensions, width, height);
        if (bestVariant) {
          setCurrentVariant(bestVariant);
          return;
        }
      }
      // Fallback to breakpoint-based selection
      const variant = selectVariant(width, height);
      setCurrentVariant(variant);
    }
  }, [photo.id, photo.variants]);

  // ========================================
  // Resize Observation (Asynchronous)
  // ========================================

  useEffect(() => {
    if (!containerRef.current) return;

    // Debounced resize handler
    const handleResize = debounce((width: number, height: number) => {
      setContainerSize({ width, height });

      // Use selectBestVariant if variants have dimension data
      let newVariant: VariantType;
      const variantsWithDimensions = photo.variants.filter(v => v.width && v.height);
      if (variantsWithDimensions.length > 0) {
        const bestVariant = selectBestVariant(variantsWithDimensions, width, height);
        newVariant = bestVariant || selectVariant(width, height);
      } else {
        newVariant = selectVariant(width, height);
      }

      // Only update if variant actually changed (prevents unnecessary image reloads)
      if (newVariant !== currentVariant) {
        setCurrentVariant(newVariant);
        setLoadingState('loading');
      }
    }, 150);

    // Create ResizeObserver
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        handleResize(width, height);
      }
    });

    observer.observe(containerRef.current);

    // Cleanup
    return () => {
      observer.disconnect();
    };
  }, [currentVariant, photo.variants]);

  // ========================================
  // Image Loading Handlers
  // ========================================

  const handleImageLoad = useCallback((event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    const dimensions = {
      width: img.naturalWidth,
      height: img.naturalHeight,
    };

    setLoadedDimensions(dimensions);
    setLoadingState('loaded');

    // Determine which variant was actually loaded (may differ from preferred due to fallback)
    const loadedUrl = img.src;
    const actualLoadedVariant = detectLoadedVariant(photo, loadedUrl);

    // Smart validation: Warn if wrong variant loaded when preferred variant exists
    // Don't warn for legitimate fallbacks (when preferred variant is missing)
    const preferredVariantExists = checkVariantExists(photo, currentVariant);
    const variantToValidate = preferredVariantExists ? currentVariant : (actualLoadedVariant || currentVariant);

    const validation = validateDimensions(
      dimensions.width,
      dimensions.height,
      variantToValidate
    );
    setValidationResult(validation);

    // Call user callback
    if (onLoadCallback) {
      onLoadCallback(dimensions);
    }
  }, [currentVariant, photo, onLoadCallback]);

  const handleImageError = useCallback((_event: React.SyntheticEvent<HTMLImageElement>) => {
    setLoadingState('error');

    // Call user callback
    if (onErrorCallback) {
      onErrorCallback(new Error('Image failed to load'));
    }
  }, [onErrorCallback]);

  // ========================================
  // Get Image URL
  // ========================================

  const imageUrl = getVariantUrl(photo, currentVariant, preferStorageUrl);

  // ========================================
  // Render
  // ========================================

  // Error state: No image available
  if (!imageUrl) {
    return (
      <div
        ref={mergedRef}
        className={`flex items-center justify-center bg-surface-2 text-muted p-4 ${className}`}
      >
        <span className="text-sm">No image available</span>
      </div>
    );
  }

  return (
    <div ref={mergedRef} className={`relative ${className}`}>
      <img
        ref={imageRef}
        src={imageUrl}
        alt={alt || photo.description || ''}
        className={imgClassName}
        style={imgStyle}
        draggable={false}
        loading="lazy"
        onLoad={handleImageLoad}
        onError={handleImageError}
      />

      {/* Debug Overlay */}
      <DebugModeOnly>
        {loadedDimensions && (
          <div className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded font-mono">
            <div className="flex items-center gap-1">
              <span>{validationResult?.valid ? '✓' : '⚠'}</span>
              <span>{currentVariant.charAt(0).toUpperCase() + currentVariant.slice(1)}</span>
              <span className="text-gray-300">
                {loadedDimensions.width}x{loadedDimensions.height}
              </span>
            </div>
            {validationResult?.warning && (
              <div className="text-yellow-400 mt-0.5">
                {validationResult.warning}
              </div>
            )}
            <div className="text-gray-400 text-[10px] mt-0.5">
              <AdminAwareIdLink id={photo.id} />
            </div>
          </div>
        )}
      </DebugModeOnly>
    </div>
  );
}
